import type { BacktestPosition, BacktestResult, CreditSpreadBacktest } from '@fleece/models';
import { useMemo, useState } from 'react';
import { OutcomeDonut, StackedHistogram } from '@/components/backtest-charts';
import { Button, Card, Message, OutcomeMarker, Spinner } from '@/components/ui';
import { usePreferences } from '@/hooks/use-preferences';
import * as fmt from '@/lib/format';
import { niceStep, stackedBins } from '@/lib/histogram';
import { outcomeStyle } from '@/lib/outcomes';
import { cn } from '@/lib/utils';

/**
 * A backtest while it runs, then what it found: the headline numbers, how positions
 * ended, how long they took, what they returned, and every position in a table — the
 * table is also where anything the charts show can be read exactly.
 */
export function BacktestResults({ backtest }: { backtest: CreditSpreadBacktest }) {
  if (backtest.status === 'running') {
    const { done, total, stage } = backtest.progress;
    return (
      <Card title="Backtest">
        <div className="flex flex-col gap-1.5 p-2.5">
          <div className="flex items-center gap-2 text-xs text-ink-2">
            <Spinner />
            {stage}
            {total > 1 && (
              <span className="tabular text-muted">
                {done} / {total}
              </span>
            )}
          </div>
          <div className="h-1 overflow-hidden rounded-full bg-surface-2">
            <div className="h-full bg-accent transition-[width]" style={{ width: `${total === 0 ? 0 : (done / total) * 100}%` }} />
          </div>
        </div>
      </Card>
    );
  }
  if (backtest.status === 'failed' || backtest.result === undefined) {
    return (
      <Card title="Backtest">
        <Message tone="error">The backtest failed: {backtest.error ?? 'no reason was given'}.</Message>
      </Card>
    );
  }
  return <Finished backtest={backtest} result={backtest.result} />;
}

function Finished({ backtest, result }: { backtest: CreditSpreadBacktest; result: BacktestResult }) {
  const { positions, stats, skips, template } = result;
  const { date } = usePreferences();

  const daysBins = useMemo(
    () =>
      stackedBins(
        positions,
        (p) => p.daysHeld,
        niceStep(
          positions.flatMap((p) => (p.daysHeld === undefined ? [] : [p.daysHeld])),
          12,
        ),
      ),
    [positions],
  );
  const returnBins = useMemo(() => {
    const returns = positions.flatMap((p) => (p.profit === undefined ? [] : [p.profit / p.credit]));
    return stackedBins(positions, (p) => (p.profit === undefined ? undefined : p.profit / p.credit), Math.max(0.1, niceStep(returns, 12)));
  }, [positions]);

  const took = backtest.finishedAt === undefined ? undefined : Math.round((backtest.finishedAt - backtest.startedAt) / 1000);

  return (
    <div className="flex flex-col gap-2">
      <Card
        title="Backtest"
        actions={
          <span className="text-[11px] text-muted tabular">
            {date(backtest.request.from)} – {date(backtest.request.to)}
            {took !== undefined && ` · ${took}s`}
          </span>
        }
      >
        <div className="grid grid-cols-2 gap-px bg-line sm:grid-cols-4 lg:grid-cols-8">
          <Tile label="Win rate" value={fmt.pct(stats.winRate, 0)} note={`${stats.closed} closed`} />
          <Tile
            label="Annualized return"
            value={fmt.pct(stats.annualizedReturnOnCapital, 0)}
            note="on capital at risk"
            tone={stats.annualizedReturnOnCapital}
            title="Σ profit ÷ Σ (max loss × days held) × 365, over closed positions. What a dollar of capital earned per year while tied up in a spread, weighted by how much and for how long. Simple, not compounded. It does not say how much capital running every position at once would need: positions overlap."
          />
          <Tile
            label="Avg return on capital"
            value={fmt.pct(stats.averageReturnOnCapital, 0)}
            note="profit ÷ max loss"
            tone={stats.averageReturnOnCapital}
            title="Each closed position's profit over its max loss (width − credit), averaged with every position counting the same."
          />
          <Tile label="Avg days held" value={fmt.days(stats.averageDaysHeld)} note={`median ${fmt.days(stats.medianDaysHeld)}`} />
          <Tile label="Avg P&L / contract" value={fmt.dollars(stats.averageProfit === undefined ? undefined : stats.averageProfit * 100)} tone={stats.averageProfit} />
          <Tile label="Total P&L" value={fmt.dollars(stats.totalProfit * 100, 0)} note="one contract each" tone={stats.totalProfit} />
          <Tile
            label="Avg return on credit"
            value={fmt.pct(stats.averageReturnOnCredit, 0)}
            note="profit ÷ credit"
            tone={stats.averageReturnOnCredit}
            title="Each closed position's profit over the credit it collected, averaged with every position counting the same."
          />
          <Tile label="Positions" value={String(stats.positions)} note={stats.open > 0 ? `${stats.open} still open` : undefined} />
        </div>
        <p className="border-t border-line px-2.5 py-1.5 text-[11px] text-muted">
          Imitating {template.right === 'put' ? 'a put' : 'a call'} spread {fmt.pct(template.shortMoneyness)} out, {fmt.pct(template.widthPct, 2)} of spot wide, legs quoted{' '}
          {fmt.pct(template.shortSpreadPct, 0)} and {fmt.pct(template.longSpreadPct, 0)} wide today. Not entered: {skips.noCandidates} sessions with nothing in range,{' '}
          {skips.noPrints} spreads a leg of which had not traded in the 30 minutes before entry, {skips.noCredit} with no credit after crossing the spread
          {backtest.request.avoidEarnings && `, ${skips.earnings} held through an earnings reaction, ${skips.earningsUnknown} expiring past the end of the earnings calendar`}
          {skips.creditAboveWidth > 0 && `, ${skips.creditAboveWidth} whose estimated credit was at least the width — prints from different moments`}.
        </p>
      </Card>

      {stats.positions === 0 ? (
        <Card>
          <Message>No positions were opened. Widen the DTE or moneyness range, or move the entry time to when the options trade more.</Message>
        </Card>
      ) : (
        <>
          <div className="grid gap-2 md:grid-cols-[auto_1fr_1fr]">
            <Card title="Outcome">
              <div className="p-2.5">
                <OutcomeDonut stats={stats} />
              </div>
            </Card>
            <Card title="Days to exit">
              <div className="p-2.5">
                <StackedHistogram bins={daysBins} label="Closed positions by calendar days held" formatBin={(bin) => `${bin.x0}–${bin.x1}d`} />
              </div>
            </Card>
            <Card title="Return on credit">
              <div className="p-2.5">
                <StackedHistogram
                  bins={returnBins}
                  label="Closed positions by profit as a share of credit"
                  formatBin={(bin) => `${Math.round(bin.x0 * 100)}%–${Math.round(bin.x1 * 100)}%`}
                />
              </div>
            </Card>
          </div>
          <PositionsTable positions={positions} />
        </>
      )}
    </div>
  );
}

function Tile({ label, value, note, tone, title }: { label: string; value: string; note?: string; tone?: number; title?: string }) {
  return (
    <div className={cn('bg-surface px-2.5 py-2', title !== undefined && 'cursor-help')} title={title}>
      <div className="text-[11px] text-ink-2">{label}</div>
      <div className={cn('text-lg leading-tight font-semibold', tone !== undefined && (tone >= 0 ? 'text-positive' : 'text-negative'))}>{value}</div>
      {note !== undefined && <div className="text-[11px] text-muted">{note}</div>}
    </div>
  );
}

const PAGE = 50;

function PositionsTable({ positions }: { positions: ReadonlyArray<BacktestPosition> }) {
  const { date } = usePreferences();
  const [shown, setShown] = useState(PAGE);
  const newestFirst = useMemo(() => [...positions].reverse(), [positions]);
  return (
    <Card title={`Positions (${positions.length})`}>
      <div className="scrollbar-thin max-h-[50vh] overflow-auto">
        <table className="w-full border-separate border-spacing-0 text-xs tabular">
          <thead className="sticky top-0 bg-surface text-[11px] text-ink-2">
            <tr>
              {['Entered', 'Spread', 'Exp', 'DTE', 'OTM', 'Credit', 'Capital', 'Outcome', 'Closed', 'Days', 'Debit', 'P&L', 'ROC', 'Ann.'].map((h, i) => (
                <th
                  key={h}
                  className={cn('border-b border-line px-2 py-1 font-medium whitespace-nowrap', i < 2 || h === 'Outcome' ? 'text-left' : 'text-right', i === 0 && 'pl-2.5')}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {newestFirst.slice(0, shown).map((p) => {
              const style = outcomeStyle(p.exitReason);
              return (
                <tr key={`${p.entryAt}-${p.shortSymbol}`} className="hover:bg-surface-2">
                  <td className="border-b border-line py-1 pr-2 pl-2.5 whitespace-nowrap">{date(p.entryDate)}</td>
                  <td className="border-b border-line px-2 py-1 whitespace-nowrap">
                    {fmt.strike(p.shortStrike)}/{fmt.strike(p.longStrike)}
                  </td>
                  <td className="border-b border-line px-2 py-1 text-right whitespace-nowrap">{date(p.expiration)}</td>
                  <td className="border-b border-line px-2 py-1 text-right">{p.dte}</td>
                  <td className="border-b border-line px-2 py-1 text-right">{fmt.pct(p.shortMoneyness)}</td>
                  <td className="border-b border-line px-2 py-1 text-right">{fmt.price(p.credit)}</td>
                  <td className="border-b border-line px-2 py-1 text-right" title="Max loss per contract: (width − credit) × 100">
                    {fmt.dollars(p.maxLoss * 100, 0)}
                  </td>
                  <td className="border-b border-line px-2 py-1 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      <OutcomeMarker color={style.color} shape={style.shape} size={7} />
                      {style.label}
                    </span>
                  </td>
                  <td className="border-b border-line px-2 py-1 text-right whitespace-nowrap">{p.exitDate === undefined ? fmt.NA : date(p.exitDate)}</td>
                  <td className="border-b border-line px-2 py-1 text-right">{p.daysHeld === undefined ? fmt.NA : p.daysHeld.toFixed(1)}</td>
                  <td className="border-b border-line px-2 py-1 text-right">{fmt.price(p.exitDebit)}</td>
                  <td className={cn('border-b border-line px-2 py-1 text-right', p.profit !== undefined && (p.profit >= 0 ? 'text-positive' : 'text-negative'))}>
                    {fmt.dollars(p.profit === undefined ? undefined : p.profit * 100, 0)}
                  </td>
                  <td className={cn('border-b border-line px-2 py-1 text-right', signTone(p.returnOnCapital))}>{fmt.pct(p.returnOnCapital, 0)}</td>
                  <td className={cn('border-b border-line px-2 py-1 text-right', signTone(p.annualizedReturn))}>{fmt.pct(p.annualizedReturn, 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {shown < positions.length && (
        <div className="border-t border-line p-1.5 text-center">
          <Button variant="ghost" onClick={() => setShown((n) => n + PAGE * 4)}>
            Show more ({positions.length - shown} left)
          </Button>
        </div>
      )}
    </Card>
  );
}

function signTone(value: number | undefined): string | undefined {
  return value === undefined ? undefined : value >= 0 ? 'text-positive' : 'text-negative';
}
